import { InputNumber } from 'antd';
import { Duration, DurationUnit, DurationObjectUnits } from 'luxon';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import generateSliderMarks, { FormatFunction } from '../../utils/generateSliderMarks';
import { useDebounce } from '../../utils/hooks';
import Slider from '../Slider.style';

enum THEME_TIME_RANGE {
    DARK = 'dark',
    LIGHT = 'light',
}

interface TimeRangeFormProps {
    value: string;
    range: string;
    unit: keyof Pick<DurationObjectUnits, 'days' | 'hours' | 'minutes'>;
    onChange(range: string): void;
    markerStep?: number;
    label?: string | null;
    step?: number;
    mode?: THEME_TIME_RANGE;
}

const formatMark =
    (mode: THEME_TIME_RANGE = THEME_TIME_RANGE.LIGHT): FormatFunction =>
    (value) => ({ style: { color: mode === THEME_TIME_RANGE.DARK ? 'white' : 'black' }, label: value });

const createMarks = (min: string, max: string, unit: DurationUnit, step = 5, mode: THEME_TIME_RANGE = THEME_TIME_RANGE.LIGHT) => {
    const start = Duration.fromISO(min);
    const end = Duration.fromISO(max);

    const startValue = start.as(unit);
    const endValue = end.as(unit);

    const marks = generateSliderMarks(startValue, endValue, step, formatMark(mode));

    return {
        min: startValue,
        max: endValue,
        marks,
        startDuration: start,
        endDuration: end,
    };
};

const convertToPercentage = (min: number, max: number, value: number) => {
    const ratio = (value - min) / (max - min);

    if (ratio > 1) {
        return 1;
    } else if (ratio < 0) {
        return 0;
    }

    return ratio;
};
/**
 * Renders a range slider and inputs to allow the user to select the day and hour range
 *
 * When a user inputs a number, immediately renders it.
 * Uses a Debounce to normalize the value to it's min/max and update the data range
 */
const DEFAULT_NEGATIVE_HOUR_VALUES = (): [boolean, boolean, boolean, boolean] => [false, false, false, false];
const addNegativeSign = (negativeHourValues: [boolean, boolean, boolean, boolean], index: number) => (negativeHourValues[index] ? '-' : '');
const UNITS = new Map<keyof Pick<DurationObjectUnits, 'days' | 'hours' | 'minutes' | 'seconds'>, string>([
    ['days', 'd'],
    ['hours', 'h'],
    ['minutes', 'm'],
    ['seconds', 's'],
]);

const getRangeDuration = (min: Duration, max: Duration, rangePercentage: [number, number]) => {
    const totalDuration = max.as('milliseconds') - min.as('milliseconds');
    const rangeStartAsMillis = Math.round(min.as('milliseconds') + totalDuration * rangePercentage[0]);
    const rangeEndAsMillis = Math.round(min.as('milliseconds') + totalDuration * rangePercentage[1]);
    const rangeStart = Duration.fromMillis(rangeStartAsMillis);
    const rangeEnd = Duration.fromMillis(rangeEndAsMillis);

    return { rangeStart, rangeEnd };
};

const TimeRangeFormGroup: React.FC<TimeRangeFormProps> = ({ label = 'Time range', range, unit, step, markerStep, value, onChange, mode }) => {
    const { marks, min, max, startDuration, endDuration, isRange } = useMemo(() => {
        const [min, max] = range.split('--');
        const isRange = value.split('--').length > 1;

        return { ...createMarks(min, max, unit, markerStep, mode), isRange };
    }, [range]);

    const [[durationUnit, durationUnitShort], [durationSubUnit, durationSubUnitShort]] = useMemo(() => {
        const unitsArray = Array.from(UNITS);
        const keys = Array.from(UNITS.keys());
        const index = keys.indexOf(unit);

        return [unitsArray[index], unitsArray[index + 1]];
    }, [unit]);

    const convert = useCallback((value: number) => convertToPercentage(min, max, value), [min, max]);

    const [valueStart, valueEnd] = (isRange ? value : `PT0M--${value}`).split('--').map((duration) => Duration.fromISO(duration).as(unit));
    const [rangePercentage, setRangePercentage] = useState<[number, number]>([convert(valueStart), convert(valueEnd)]);
    const [negativeHourValues, setNegativeTotalHourValues] = useState(DEFAULT_NEGATIVE_HOUR_VALUES());

    const { rangeStart, rangeEnd } = useMemo(
        () => getRangeDuration(startDuration, endDuration, rangePercentage),
        [startDuration, endDuration, rangePercentage]
    );
    const rangeStartDuration = rangeStart.shiftTo(durationUnit, durationSubUnit);
    const rangeEndDuration = rangeEnd.shiftTo(durationUnit, durationSubUnit);
    const rangeStartDurationObject = rangeStartDuration.toObject();
    const rangeEndDurationObject = rangeEndDuration.toObject();

    const debouncedRangePercentage = useDebounce(rangePercentage, 300);
    const [debouncedStart, debouncedEnd] = debouncedRangePercentage;

    useEffect(() => {
        onChange(isRange ? `${rangeStartDuration.toISO()}--${rangeEndDuration.toISO()}` : rangeEndDuration.toISO());
    }, [debouncedStart, debouncedEnd]);

    const timeParser = (charToReplace: string, index: number) => (value: string) => {
        const time = value.replace(charToReplace, '');

        if (time === '-') {
            const negativeHourValues = DEFAULT_NEGATIVE_HOUR_VALUES();
            negativeHourValues[index] = true;

            setNegativeTotalHourValues(negativeHourValues);
            return 0;
        } else if (negativeHourValues[index]) {
            setNegativeTotalHourValues(DEFAULT_NEGATIVE_HOUR_VALUES());
        }

        return time.length > 0 ? Number.parseInt(time, 10) : 0;
    };

    return (
        <>
            {label && (
                <div style={{ gridColumn: 1 }}>
                    <strong>{label}:</strong>
                </div>
            )}
            <div style={{ display: 'grid', gridColumn: '2 / 6', gridTemplateColumns: `repeat(${isRange ? '2' : '1'}, 1fr)`, gap: '10px' }}>
                {isRange && (
                    <div>
                        <InputNumber
                            value={rangeStartDurationObject[durationUnit]}
                            step={1}
                            onChange={(value) => {
                                const updated = rangeStartDuration.set({ [durationUnit]: value }).as(unit);

                                setRangePercentage([convert(updated), rangePercentage[1]]);
                            }}
                            formatter={(value) => `${addNegativeSign(negativeHourValues, 0)}${value}${durationUnitShort}`}
                            parser={timeParser(durationUnitShort, 0)}
                            style={{ width: '50%' }}
                        />
                        <InputNumber
                            value={rangeStartDurationObject[durationSubUnit]}
                            step={1}
                            onChange={(value) => {
                                const updated = rangeStartDuration.set({ [durationSubUnit]: value }).as(unit);

                                setRangePercentage([convert(updated), rangePercentage[1]]);
                            }}
                            formatter={(value) => `${addNegativeSign(negativeHourValues, 1)}${value}${durationSubUnitShort}`}
                            parser={timeParser(durationSubUnitShort, 1)}
                            style={{ width: '50%' }}
                        />
                    </div>
                )}
                <div>
                    <InputNumber
                        value={rangeEndDurationObject[durationUnit]}
                        step={1}
                        onChange={(value) => {
                            const updated = rangeEndDuration.set({ [durationUnit]: value }).as(unit);

                            setRangePercentage([rangePercentage[0], convert(updated)]);
                        }}
                        formatter={(value) => `${addNegativeSign(negativeHourValues, 2)}${value}${durationUnitShort}`}
                        parser={timeParser(durationUnitShort, 2)}
                        style={{ width: '50%' }}
                    />
                    <InputNumber
                        value={rangeEndDurationObject[durationSubUnit]}
                        step={1}
                        onChange={(value) => {
                            const updated = rangeEndDuration.set({ [durationSubUnit]: value }).as(unit);

                            setRangePercentage([rangePercentage[0], convert(updated)]);
                        }}
                        formatter={(value) => `${addNegativeSign(negativeHourValues, 3)}${value}${durationSubUnitShort}`}
                        parser={timeParser(durationSubUnitShort, 3)}
                        style={{ width: '50%' }}
                    />
                </div>
            </div>
            <div style={{ gridColumn: '2 / 6', marginTop: '-1em' }}>
                {/* @ts-ignore todo */}
                <Slider
                    range={isRange}
                    marks={marks}
                    min={min}
                    max={max}
                    onChange={(value: number | [number, number]) => {
                        if (isRange) {
                            const [start, end] = value as [number, number];

                            setRangePercentage([convert(start), convert(end)]);
                        } else {
                            setRangePercentage([convert(0), convert(value as number)]);
                        }

                        setNegativeTotalHourValues(DEFAULT_NEGATIVE_HOUR_VALUES());
                    }}
                    value={isRange ? ([rangeStart.as(unit), rangeEnd.as(unit)] as [number, number]) : rangeEnd.as(unit)}
                    step={step}
                    tipFormatter={(value) => {
                        const duration = Duration.fromObject({ [unit]: value }).shiftTo(durationUnit, durationSubUnit);

                        return `${duration[durationUnit]}${durationUnitShort} ${duration[durationSubUnit]}${durationSubUnitShort} (${duration.toISO()})`;
                    }}
                />
            </div>
        </>
    );
};

export default TimeRangeFormGroup;
export { THEME_TIME_RANGE };
